// Global Instructions Rule Applied!
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { createProspect } from '../../app_modules/fundraising/services/fundraisingService.ts'
import type { ProspectRecord } from '../../state_manager/interfaces/fundraising.ts'
import type { AuthState } from '../../core/auth/authSlice.ts'

export interface ProspectsState {
  prospects: ProspectRecord[]
  loading: boolean
  submitting: boolean
  error: string | null
  submitError: string | null
}

// Initial state
const initialState: ProspectsState = {
  prospects: [],
  loading: false,
  submitting: false,
  error: null,
  submitError: null
}

// Async thunk for creating a new prospect
export const createProspectThunk = createAsyncThunk<
  ProspectRecord | null,
  Parameters<typeof createProspect>[0],
  { state: { auth: AuthState }; rejectValue: string }
>('prospects/createProspect', async (prospectData, { rejectWithValue, getState }) => {
  try {
    // Get access token from auth state
    const accessToken = getState().auth.accessToken

    const response = await createProspect(prospectData, accessToken)

    if (!response.success) {
      console.error('ProspectsSlice.createProspectThunk failed:', response.error)
      return rejectWithValue(response.error ?? 'Failed to create prospect')
    }

    return response.data ?? null
  } catch (error) {
    console.error('ProspectsSlice.createProspectThunk error:', error)
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to create prospect')
  }
})

// Prospects slice
const prospectsSlice = createSlice({
  name: 'prospects',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null
      state.submitError = null
    },

    // Add a new prospect
    addProspect: (state, action: PayloadAction<ProspectRecord>) => {
      state.prospects.unshift(action.payload)
    }
  },
  extraReducers: (builder) => {
    builder
      // Create prospect
      .addCase(createProspectThunk.pending, (state) => {
        state.submitting = true
        state.submitError = null
      })
      .addCase(createProspectThunk.fulfilled, (state, action) => {
        state.submitting = false
        state.submitError = null
        // Add new prospect to the beginning of the array
        if (action.payload) state.prospects.unshift(action.payload)
      })
      .addCase(createProspectThunk.rejected, (state, action) => {
        state.submitting = false
        state.submitError = action.payload ?? 'Failed to create prospect'
      })
  }
})

const { addProspect, clearError } = prospectsSlice.actions

type ProspectsRoot = { prospects: ProspectsState }

// Selectors
const selectProspects = (state: ProspectsRoot) => state.prospects.prospects
const selectProspectsLoading = (state: ProspectsRoot) => state.prospects.loading
const selectProspectsSubmitting = (state: ProspectsRoot) => state.prospects.submitting
const selectProspectsError = (state: ProspectsRoot) => state.prospects.error
const selectProspectsSubmitError = (state: ProspectsRoot) => state.prospects.submitError

export default prospectsSlice.reducer
